// Every web module, registered here. Pages are lazy-loaded so each area only downloads what it uses.
import { lazy, type ComponentType } from 'react'
import { BILL_READ_PERMISSIONS, PURCHASE_ORDER_READ_PERMISSIONS, type Area, type PermissionRequirement } from '@briary/shared'
import { SUPERADMIN_ONLY } from '../lib/auth'
import { NAV_GROUP_ORDER, type NavItem, type WebModule } from './types'

type Page = ComponentType<{ params: Record<string, string> }>
/** Lazy-load a named export from a page module. */
const named = <M extends Record<string, unknown>>(load: () => Promise<M>, name: keyof M) =>
  lazy(async () => ({ default: (await load())[name] as Page }))

const projectPages = () => import('./projects/pages')

const adminHome: WebModule = {
  id: 'admin-home',
  area: 'admin',
  nav: [{ group: 'Overview', items: [{ label: 'Dashboard', path: '/admin', icon: 'home' }] }],
  routes: [{ path: '/admin', component: lazy(() => import('./admin/Dashboard')) }],
}

const adminProjects: WebModule = {
  id: 'admin-projects',
  area: 'admin',
  nav: [
    {
      group: 'Work',
      items: [
        { label: 'Projects', path: '/admin/projects', icon: 'folder', perm: 'projects.view' },
        { label: 'Companies', path: '/admin/companies', icon: 'building', perm: 'projects.view' },
        { label: 'Contacts', path: '/admin/contacts', icon: 'users', perm: 'projects.view' },
      ],
    },
  ],
  routes: [
    { path: '/admin/projects', component: named(projectPages, 'AdminProjects'), perm: 'projects.view' },
    { path: '/admin/projects/:id', component: named(projectPages, 'AdminProject'), perm: 'projects.view' },
    { path: '/admin/project-register', component: named(projectPages, 'AdminBusinessProjects'), perm: 'projects.view' },
    { path: '/admin/project-register/:id', component: named(projectPages, 'AdminBusinessProject'), perm: 'projects.view' },
    { path: '/admin/companies', component: named(projectPages, 'AdminCompanies'), perm: 'projects.view' },
    { path: '/admin/contacts', component: named(projectPages, 'AdminContacts'), perm: 'projects.view' },
    { path: '/admin/contacts/managers/:id', component: named(projectPages, 'AdminManager'), perm: 'users.view' },
    { path: '/admin/companies/:id', component: named(projectPages, 'AdminCompany'), perm: 'projects.view' },
  ],
}

const adminUsers: WebModule = {
  id: 'admin-users',
  area: 'admin',
  nav: [
    {
      group: 'Admin',
      items: [
        { label: 'Users', path: '/admin/users', icon: 'users', perm: 'users.view' },
        { label: 'Audit log', path: '/admin/audit', icon: 'history', perm: 'audit.view' },
        { label: 'Email log', path: '/admin/emails', icon: 'history', perm: 'emails.view' },
        { label: 'Settings', path: '/admin/settings', icon: 'settings', perm: SUPERADMIN_ONLY },
        { label: 'Switch-over', path: '/admin/switch-over', icon: 'swap', perm: SUPERADMIN_ONLY },
        { label: 'Database', path: '/admin/database', icon: 'grid', perm: SUPERADMIN_ONLY },
        { label: 'Processing costs', path: '/admin/costs', icon: 'invoice', perm: SUPERADMIN_ONLY },
      ],
    },
  ],
  routes: [
    { path: '/admin/users', component: lazy(() => import('./admin/Users')), perm: 'users.view' },
    { path: '/admin/users/:id', component: lazy(() => import('./admin/UserDetail')), perm: 'users.view' },
    { path: '/admin/roles', component: lazy(() => import('./admin/Roles')), perm: 'users.view' },
    { path: '/admin/audit', component: lazy(() => import('./admin/Audit')), perm: 'audit.view' },
    { path: '/admin/emails', component: lazy(() => import('./admin/EmailLog')), perm: 'emails.view' },
    { path: '/admin/settings', component: lazy(() => import('./admin/Settings')), perm: SUPERADMIN_ONLY },
    { path: '/admin/switch-over', component: lazy(() => import('./admin/Migration')), perm: SUPERADMIN_ONLY },
    { path: '/admin/database', component: lazy(() => import('./admin/Database')), perm: SUPERADMIN_ONLY },
    { path: '/admin/costs', component: lazy(() => import('./admin/Costs')), perm: SUPERADMIN_ONLY },
  ],
}

const portalHome: WebModule = {
  id: 'portal-home',
  area: 'portal',
  nav: [
    { group: 'Overview', items: [{ label: 'Dashboard', path: '/portal', icon: 'home' }] },
    // "Alternative photo evidence" is off the menu (Chris, 3 Nov 2026); the page still opens from a plot's Sign declaration.
    { group: 'My sites', items: [{ label: 'Sites & plots', path: '/portal/sites', icon: 'folder' }] },
  ],
  routes: [
    { path: '/portal', component: lazy(() => import('./portal/Dashboard')) },
    { path: '/portal/sites', component: named(projectPages, 'PortalSites') },
    { path: '/portal/sites/:id', component: named(projectPages, 'PortalSite') },
    { path: '/portal/plots/:id/photos', component: lazy(() => import('./photos/PlotPhotosPage')) },
    { path: '/portal/photos/declarations', component: lazy(() => import('./photos/Declarations')) },
    { path: '/portal/photos/declarations/:plot', component: lazy(() => import('./photos/Declarations')) },
  ],
}

const subconPages = () => import('./subcon/pages')
const subconHome: WebModule = {
  id: 'subcon-home',
  area: 'subcon',
  nav: [
    { group: 'Overview', items: [{ label: 'Dashboard', path: '/subcon', icon: 'home' }] },
    {
      group: 'Air testing',
      items: [
        { label: 'Bookings', path: '/subcon/bookings', icon: 'wind' },
        { label: 'Calendar', path: '/subcon/calendar', icon: 'calendar' },
      ],
    },
  ],
  routes: [
    { path: '/subcon', component: lazy(() => import('./subcon/Dashboard')) },
    { path: '/subcon/bookings', component: named(subconPages, 'SubconBookings') },
    { path: '/subcon/calendar', component: named(subconPages, 'SubconCalendar') },
    { path: '/subcon/sites/:id', component: named(subconPages, 'SubconSite') },
  ],
}

// EPC, BREL and lodgement; air testing (admin + client portal).
const epcPages = () => import('./epc/pages')
const airPages = () => import('./airtests/pages')
const adminEpc: WebModule = {
  id: 'admin-epc',
  area: 'admin',
  nav: [
    {
      group: 'Work',
      items: [
        { label: 'EPC & lodgement', path: '/admin/epc', icon: 'bolt', perm: 'epc.view' },
        { label: 'Air testing', path: '/admin/air-tests', icon: 'wind', perm: 'airtests.view' },
      ],
    },
  ],
  routes: [
    { path: '/admin/epc', component: named(epcPages, 'AdminEpcList'), perm: 'epc.view' },
    { path: '/admin/epc/tools', component: named(epcPages, 'AdminEpcTools'), perm: 'epc.view' },
    { path: '/admin/epc/:id', component: named(epcPages, 'AdminEpcDetail'), perm: 'epc.view' },
    { path: '/admin/air-tests', component: named(airPages, 'AdminAirTestsPage'), perm: 'airtests.view' },
    { path: '/admin/air-tests/tools', component: named(airPages, 'AdminAirTestToolsPage'), perm: 'airtests.view' },
  ],
}
const portalEpc: WebModule = {
  id: 'portal-epc',
  area: 'portal',
  nav: [
    {
      group: 'My sites',
      items: [
        { label: 'EPCs & BRELs', path: '/portal/epc', icon: 'bolt' },
        { label: 'Air tests', path: '/portal/air-tests', icon: 'wind' },
        { label: 'Air test calendar', path: '/portal/air-tests/calendar', icon: 'calendar' },
      ],
    },
  ],
  routes: [
    { path: '/portal/epc', component: named(epcPages, 'PortalEpc') },
    { path: '/portal/air-tests', component: named(airPages, 'PortalAirTestsPage') },
    { path: '/portal/air-tests/calendar', component: named(airPages, 'PortalAirCalendarPage') },
  ],
}

// Kaizen tools. One lazy page per tool serves both its list and project routes, so opening a project keeps the app mounted.
const WlcPage = lazy(() => import('./wlc/Page'))
const GridPage = lazy(() => import('./grid/Page'))
const StatementsPage = lazy(() => import('./statements/Page'))

const adminWlc: WebModule = {
  id: 'wlc',
  area: 'admin',
  nav: [{ group: 'Tools', items: [{ label: 'Whole life carbon', path: '/admin/wlc', icon: 'leaf', perm: 'wlc.use' }] }],
  routes: [
    { path: '/admin/wlc', component: WlcPage, perm: 'wlc.use' },
    { path: '/admin/wlc/:id', component: WlcPage, perm: 'wlc.use' },
  ],
}

// Kaizen PSI opens full screen (App.tsx); these entries give the nav item and "No access" to users without psi.use.
const PsiStartPage = lazy(() => import('./psi/app/PsiStart'))
const adminPsi: WebModule = {
  id: 'psi',
  area: 'admin',
  nav: [{ group: 'Tools', items: [{ label: 'Kaizen PSI', path: '/admin/psi/app', icon: 'grid', perm: 'psi.use' }] }],
  routes: [
    { path: '/admin/psi/app', component: PsiStartPage, perm: 'psi.use' },
    { path: '/admin/psi/app/:id', component: PsiStartPage, perm: 'psi.use' },
  ],
}

const adminGrid: WebModule = {
  id: 'grid',
  area: 'admin',
  nav: [{ group: 'Tools', items: [{ label: 'Grid capacity', path: '/admin/grid', icon: 'bolt', perm: 'grid.use' }] }],
  routes: [
    { path: '/admin/grid', component: GridPage, perm: 'grid.use' },
    { path: '/admin/grid/:id', component: GridPage, perm: 'grid.use' },
  ],
}

// Energy & sustainability statements (docs/STATEMENTS.md); the policy library is its own page. The policies route is
// listed before :id so it is matched first.
const adminStatements: WebModule = {
  id: 'statements',
  area: 'admin',
  nav: [
    {
      group: 'Tools',
      items: [
        { label: 'Statements', path: '/admin/statements', icon: 'leaf', perm: 'statements.use' },
        { label: 'Planning policies', path: '/admin/statements/policies', icon: 'book', perm: 'statements.policies' },
      ],
    },
  ],
  routes: [
    { path: '/admin/statements', component: StatementsPage, perm: 'statements.use' },
    { path: '/admin/statements/policies', component: lazy(() => import('./statements/PoliciesPage')), perm: 'statements.policies' },
    { path: '/admin/statements/:id', component: StatementsPage, perm: 'statements.use' },
  ],
}

// ---- Operations, team and staff tools (ops, comms, hr, training, staff-misc) ----
const opsPages = () => import('./ops/pages')
const commsPages = () => import('./comms/pages')
const hrPages = () => import('./hr/pages')
const trainingPages = () => import('./training/pages')
const staffMiscPages = () => import('./staff-misc/pages')

const adminOps: WebModule = {
  id: 'admin-ops',
  area: 'admin',
  nav: [
    {
      group: 'Work',
      items: [
        { label: 'Tasks', path: '/admin/tasks', icon: 'clipboard', perm: 'tasks.view' },
        { label: 'Operations', path: '/admin/operations', icon: 'board', perm: 'tasks.view' },
        { label: 'NHBC', path: '/admin/nhbc', icon: 'shield', perm: 'nhbc.manage' },
        { label: 'Client feedback', path: '/admin/feedback', icon: 'chat', perm: 'feedback.manage' },
      ],
    },
  ],
  routes: [
    { path: '/admin/tasks', component: named(opsPages, 'AdminTasks'), perm: 'tasks.view' },
    // before /admin/tasks/:id (the first matching route wins)
    { path: '/admin/tasks/calendar', component: named(opsPages, 'AdminTaskCalendar'), perm: 'tasks.view' },
    { path: '/admin/tasks/templates', component: named(opsPages, 'AdminTaskTemplates'), perm: 'tasks.view' },
    { path: '/admin/tasks/settings', component: named(opsPages, 'AdminTaskSettings'), perm: 'tasks.view' },
    { path: '/admin/tasks/feedback', component: named(opsPages, 'AdminTaskFeedback'), perm: 'tasks.view' },
    { path: '/admin/tasks/:id', component: named(opsPages, 'AdminTask'), perm: 'tasks.view' },
    { path: '/admin/operations', component: named(opsPages, 'AdminOperations'), perm: 'tasks.view' },
    { path: '/admin/nhbc', component: named(opsPages, 'AdminNhbc'), perm: 'nhbc.manage' },
    { path: '/admin/feedback', component: named(opsPages, 'AdminFeedback'), perm: 'feedback.manage' },
    { path: '/admin/site-emails/:siteId', component: named(opsPages, 'AdminSiteEmails'), perm: 'projects.view' },
  ],
}

const adminTeam: WebModule = {
  id: 'admin-team',
  area: 'admin',
  nav: [
    {
      group: 'Team',
      items: [
        { label: 'Chat', path: '/admin/chat', icon: 'chat', perm: 'chat.use' },
        { label: 'HR', path: '/admin/hr', icon: 'calendar', perm: 'hr.self' },
        { label: 'HR admin', path: '/admin/hr/manage', icon: 'users', perm: 'hr.manage' },
        { label: 'Training', path: '/admin/training', icon: 'book', perm: 'training.view' },
        { label: 'Garments', path: '/admin/garments', icon: 'shirt', perm: 'hr.self' },
        { label: 'H&S documents', path: '/admin/health-safety', icon: 'shield', perm: 'healthsafety.manage' },
      ],
    },
    { group: 'Tools', items: [{ label: 'Briary Assist', path: '/admin/assist', icon: 'mail', perm: 'assist.manage' }] },
  ],
  routes: [
    { path: '/admin/chat', component: named(commsPages, 'AdminChat'), perm: 'chat.use' },
    { path: '/admin/assist', component: named(commsPages, 'AdminAssist'), perm: 'assist.manage' },
    { path: '/admin/hr', component: named(hrPages, 'AdminHr'), perm: 'hr.self' },
    { path: '/admin/hr/manage', component: named(hrPages, 'AdminHrManage'), perm: 'hr.manage' },
    { path: '/admin/hr/employees/:id', component: named(hrPages, 'AdminHrEmployee'), perm: 'hr.manage' },
    { path: '/admin/training', component: named(trainingPages, 'AdminTraining'), perm: 'training.view' },
    { path: '/admin/training/manage', component: named(trainingPages, 'AdminTrainingManage'), perm: 'training.manage' },
    { path: '/admin/training/courses/:slug', component: named(trainingPages, 'AdminTrainingCourse'), perm: 'training.view' },
    { path: '/admin/training/exams/:course/:exam', component: named(trainingPages, 'AdminTrainingExam'), perm: 'training.view' },
    { path: '/admin/garments', component: named(staffMiscPages, 'AdminGarments'), perm: 'hr.self' },
    { path: '/admin/health-safety', component: named(staffMiscPages, 'AdminHealthSafety'), perm: 'healthsafety.manage' },
  ],
}

const portalTeam: WebModule = {
  id: 'portal-staff-tools',
  area: 'portal',
  nav: [
    {
      group: 'Help',
      items: [
        { label: 'Health & safety', path: '/portal/health-safety', icon: 'shield' },
        { label: 'Feedback', path: '/portal/feedback', icon: 'chat' },
      ],
    },
  ],
  routes: [
    { path: '/portal/health-safety', component: named(staffMiscPages, 'PortalHealthSafety') },
    { path: '/portal/feedback', component: named(opsPages, 'PortalFeedback') },
  ],
}

const adminPhotos: WebModule = {
  id: 'admin-photos',
  area: 'admin',
  // "Alternative evidence review" is off the menu (Chris, 3 Nov 2026: staff only need to know a declaration was signed,
  // which the plot shows). The page still opens at its address.
  nav: [{ group: 'Work', items: [{ label: 'Photo review', path: '/admin/photos', icon: 'camera', perm: 'photos.review' }] }],
  routes: [
    { path: '/admin/photos', component: lazy(() => import('./photos/Review')), perm: 'photos.review' },
    { path: '/admin/photos/declarations', component: lazy(() => import('./photos/DeclarationReview')), perm: 'photos.review' },
  ],
}

const salesPages = () => import('./sales/pages')
const moneyPages = () => import('./money/pages')

const adminSales: WebModule = {
  id: 'admin-sales',
  area: 'admin',
  nav: [
    { group: 'Work', items: [{ label: 'Enquiries', path: '/admin/enquiries', icon: 'clipboard', perm: 'enquiries.view', countUrl: '/api/sales/work-requests/count' }] },
    {
      group: 'Money',
      items: [
        { label: 'Quotes', path: '/admin/quotes', icon: 'file', perm: 'quotes.view' },
        { label: 'Invoices', path: '/admin/invoices', icon: 'invoice', perm: 'invoices.view' },
        { label: 'Scoro invoice history', path: '/admin/money/scoro-history', icon: 'history', perm: 'invoices.view' },
        { label: 'Debtors', path: '/admin/money/debtors', icon: 'pound', perm: 'invoices.view' },
        { label: 'Bank', path: '/admin/money/bank', icon: 'swap', perm: 'bank.manage' },
        { label: 'Bills', path: '/admin/money/bills', icon: 'invoice', perm: BILL_READ_PERMISSIONS },
        { label: 'Purchase orders', path: '/admin/money/purchase-orders', icon: 'clipboard', perm: PURCHASE_ORDER_READ_PERMISSIONS },
        { label: 'Products & prices', path: '/admin/money/products', icon: 'grid', perm: 'quotes.view' },
        { label: 'Xero & Scoro', path: '/admin/money/xero', icon: 'key', perm: 'xero.manage' },
        { label: 'Money settings', path: '/admin/money/settings', icon: 'settings', perm: 'invoices.manage' },
      ],
    },
  ],
  routes: [
    { path: '/admin/enquiries', component: named(salesPages, 'Enquiries'), perm: 'enquiries.view' },
    { path: '/admin/enquiries/inbox', component: lazy(() => import('./comms/IntakeInbox')), perm: 'enquiries.view' },
    { path: '/admin/enquiries/inbox/:id', component: lazy(() => import('./comms/IntakeInbox')), perm: 'enquiries.view' },
    { path: '/admin/enquiries/requests', component: named(salesPages, 'Requests'), perm: 'enquiries.view' },
    { path: '/admin/enquiries/:id', component: named(salesPages, 'EnquiryDetail'), perm: 'enquiries.view' },
    { path: '/admin/enquiries/:id/pricing', component: named(salesPages, 'EnquiryPricing'), perm: 'enquiries.view' },
    // full screen in App.tsx (sales/pack/QuotePack); listed so the path is known
    { path: '/admin/enquiries/:id/pack', component: named(salesPages, 'EnquiryPricing'), perm: 'enquiries.view' },
    { path: '/admin/quotes', component: named(salesPages, 'Quotes'), perm: 'quotes.view' },
    { path: '/admin/quotes/new', component: named(salesPages, 'NewQuote'), perm: 'quotes.manage' },
    { path: '/admin/quotes/:id', component: named(salesPages, 'QuotePage'), perm: 'quotes.view' },
    { path: '/admin/money/products', component: named(salesPages, 'Products'), perm: 'quotes.view' },
    { path: '/admin/invoices', component: named(moneyPages, 'Invoices'), perm: 'invoices.view' },
    { path: '/admin/money/scoro-history', component: lazy(() => import('./money/ScoroInvoiceHistory')), perm: 'invoices.view' },
    { path: '/admin/money/scoro-history/:id', component: lazy(() => import('./money/ScoroInvoiceHistory')), perm: 'invoices.view' },
    { path: '/admin/invoices/new', component: named(moneyPages, 'NewInvoice'), perm: 'invoices.manage' },
    { path: '/admin/invoices/:id', component: named(moneyPages, 'InvoicePage'), perm: ['invoices.view',...BILL_READ_PERMISSIONS] },
    { path: '/admin/money/debtors', component: named(moneyPages, 'Debtors'), perm: 'invoices.view' },
    { path: '/admin/money/statements/:id', component: named(moneyPages, 'Statement'), perm: 'invoices.view' },
    { path: '/admin/money/bank', component: named(moneyPages, 'Bank'), perm: 'bank.manage' },
    { path: '/admin/money/bills', component: named(moneyPages, 'Bills'), perm: BILL_READ_PERMISSIONS },
    { path: '/admin/money/purchase-orders', component: named(moneyPages, 'PurchaseOrders'), perm: PURCHASE_ORDER_READ_PERMISSIONS },
    { path: '/admin/money/purchase-orders/:id', component: named(moneyPages, 'PurchaseOrderPage'), perm: PURCHASE_ORDER_READ_PERMISSIONS },
    { path: '/admin/money/xero', component: named(moneyPages, 'Xero'), perm: 'xero.manage' },
    { path: '/admin/money/settings', component: named(moneyPages, 'MoneySettings'), perm: 'invoices.view' },
  ],
}

/** The old manager portal showed requested jobs with their quote status (manager/requested_jobs.php). */
const portalSales: WebModule = {
  id: 'portal-sales',
  area: 'portal',
  nav: [{ group: 'My sites', items: [{ label: 'Requested jobs', path: '/portal/requests', icon: 'clipboard' }] }],
  routes: [{ path: '/portal/requests', component: named(salesPages, 'PortalRequests') }],
}

// Kaizen SAP: cross-site plot search, plot views/editor and API tokens (the project page has its own SAP tab).
const SapAdminPage = lazy(() => import('./sap/AdminPage'))
const adminSap: WebModule = {
  id: 'sap',
  area: 'admin',
  // Kaizen SAP opens the full-screen program (start screen → File → Open Project); the cross-site plot search and
  // API tokens stay at /admin/sap, reached from the program's File and Admin menus.
  nav: [{ group: 'Tools', items: [{ label: 'Kaizen SAP', path: '/admin/sap/app', icon: 'grid', perm: 'sap.view' }] }],
  routes: [
    { path: '/admin/sap', component: SapAdminPage, perm: 'sap.view' },
    { path: '/admin/sap/plots/:id', component: SapAdminPage, perm: 'sap.view' },
    // The full-screen app (App.tsx renders it without the shell); listed here so users without sap.view get "No access".
    { path: '/admin/sap/app', component: SapAdminPage, perm: 'sap.view' },
    { path: '/admin/sap/app/:siteId', component: SapAdminPage, perm: 'sap.view' },
    { path: '/admin/sap/window-report/:siteId', component: SapAdminPage, perm: 'sap.view' },
  ],
}

// Home Energy Model: the per-project tab lives on the project page; a dwelling opens as its own page.
const adminHem: WebModule = {
  id: 'hem',
  area: 'admin',
  routes: [
    { path: '/admin/hem/dwellings/:id', component: lazy(() => import('./hem/HemDwellingPage')), perm: 'sap.view' },
    // the report opens full screen (App.tsx); listed so users without sap.view get "No access"
    { path: '/admin/hem/runs/:id/report', component: lazy(() => import('./hem/HemDwellingPage')), perm: 'sap.view' },
  ],
}

// Part G water efficiency: a calculation per Kaizen SAP dwelling (keyed by its sap_plots id) and a project's list.
const adminPartG: WebModule = {
  id: 'partg',
  area: 'admin',
  routes: [
    { path: '/admin/partg/sites/:siteId', component: lazy(() => import('./partg/PartGProjectTab')), perm: 'sap.view' },
    { path: '/admin/partg/dwellings/:id', component: lazy(() => import('./partg/PartGPage')), perm: 'sap.view' },
    // the report opens full screen (App.tsx); listed so users without sap.view get "No access"
    { path: '/admin/partg/dwellings/:id/report', component: lazy(() => import('./partg/PartGPage')), perm: 'sap.view' },
  ],
}

// Home Energy Model for client managers (docs/HEM-PORTAL.md): the homes shared with them and their versions; a run's
// report opens full screen (App.tsx).
const portalHem: WebModule = {
  id: 'portal-hem',
  area: 'portal',
  nav: [{ group: 'My sites', items: [{ label: 'Home Energy Model', path: '/portal/hem', icon: 'leaf' }] }],
  routes: [
    { path: '/portal/hem', component: lazy(() => import('./hem/portal/PortalHem')) },
    { path: '/portal/hem/versions/:id', component: lazy(() => import('./hem/portal/PortalVersion')) },
  ],
}

// Briary Site Manager: the list lives here; /admin/siteplans/:id opens full screen (App.tsx renders it without the shell).
const sitePlanPages = () => import('./siteplans/lists')
const adminSitePlans: WebModule = {
  id: 'siteplans',
  area: 'admin',
  nav: [{ group: 'Tools', items: [{ label: 'Site plans', path: '/admin/siteplans', icon: 'grid', perm: 'siteplans.view' }] }],
  routes: [
    { path: '/admin/siteplans', component: named(sitePlanPages, 'AdminSitePlans'), perm: 'siteplans.view' },
    { path: '/admin/siteplans/:id', component: named(sitePlanPages, 'AdminSitePlans'), perm: 'siteplans.view' },
  ],
}

// Drawings & models: the per-project tab lives on the project page; this is the cross-project list of recent builds.
const adminModels: WebModule = {
  id: 'models',
  area: 'admin',
  nav: [{ group: 'Tools', items: [{ label: 'SketchUp models', path: '/admin/models', icon: 'building', perm: 'models.view' }] }],
  routes: [{ path: '/admin/models', component: lazy(() => import('./models/RecentModels')), perm: 'models.view' }],
}

/** Features still being built, shown greyed out as "Soon". Remove a line when its module goes live. */
const roadmap: WebModule = {
  id: 'roadmap',
  area: 'admin',
  nav: [
  ],
  routes: [],
}

export const WEB_MODULES: WebModule[] = [adminHome, adminProjects, adminPhotos, adminUsers, portalHome, subconHome, adminWlc, adminGrid, adminStatements, adminPsi, adminOps, adminTeam, portalTeam, adminSales, portalSales, adminSap, adminEpc, portalEpc, adminSitePlans, adminModels, adminHem, portalHem, adminPartG, roadmap]

export function navFor(area: Area, can: (perm: PermissionRequirement) => boolean): { label: string; items: NavItem[] }[] {
  const groups = new Map<string, NavItem[]>()
  for (const m of WEB_MODULES) {
    if (m.area !== area) continue
    for (const g of m.nav ?? []) {
      const items = g.items.filter((i) => !i.perm || can(i.perm))
      if (items.length) groups.set(g.group, [...(groups.get(g.group) ?? []), ...items])
    }
  }
  const order = NAV_GROUP_ORDER[area]
  return [...groups.entries()]
    .sort(([a], [b]) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99))
    .map(([label, items]) => ({ label, items }))
}

export const routesFor = (area: Area) => WEB_MODULES.filter((m) => m.area === area).flatMap((m) => m.routes)
